import { StatisticsLiveData } from "../../types/types";
import { AppLocalization } from "../localization/AppLocalization";
import { AppLocalStorage } from "./AppLocalStorage";
import { VariationId } from "../screen/TableScreen";

// live stats data for each variation
import belote_statistics_default from "../data/belote/belote_statistics_data.json";
import coinche_statistics_default from "../data/coinche/coinche_statistics_data.json";
import contree_statistics_default from "../data/contree/contree_statistics_data.json";
import { AILevel } from "../ai/TableAI";
import { JSONDeepClone } from "../../utils/JSONDeepClone";
import GameScene from "../../scenes/GameScene";


/**
 * Store these values to localStorage to preserve minimal lifetime Intermediate data, which is parsed into @see ImmediateStatsItemKeys data for display in Statistics Screen
 */
export enum IntermediateStatsItemKeys {
    /**
     * Number of Games played
     * NOTE: Game is composed of multiple Deals
     */
    numGamesPlayed = "numGamesPlayed",
    /**
     * Number of Games won by us
     */
    numGamesWonUs = "numGamesWonUs",
    /**
     * Number of Deals played
     */
    numDealsPlayed = "numDealsPlayed",
    /**
     * Number of Deals won by us
     */
    numDealsWonUs = "numDealsWonUs",
    /**
     * Number of deals we were in attack
     * NOTE: Attackers: team that took faceup card (Belote) || team that called contract (Coinche|Contree)
     */
    numAttackDealsUs = "numAttackDealsUs",
    /**
     * Number of deals we were in attack and won (fulfilled the contract)
     * NOTE: Attackers: team that took faceup card (Belote) || team that called contract (Coinche|Contree)
     */
    numAttackDealsWonUs = "numAttackDealsWonUs",
    /**
     * Number of deals we were in defense
     * NOTE: Attackers: team that took faceup card (Belote) || team that called contract (Coinche|Contree)
     */
    numDefenseDealsUs = "numDefenseDealsUs",
    /**
     * Number of deals we were in defense and won
     * NOTE: Attackers: team that took faceup card (Belote) || team that called contract (Coinche|Contree)
     */
    numDefenseDealsWonUs = "numDefenseDealsWonUs",
    /**
     * Number of deals that the Player called contract (Coinche|Contree) or took faceup card (Belote)
     */
    numAttackDealsPlayer = "numAttackDealsPlayer",
    /**
     * Number of deals that the Player called contract (Coinche|Contree) or took faceup card (Belote) AND WON.
     */
    numAttackDealsWonPlayer = "numAttackDealsWonPlayer",
    /**
     * Total score over all Deals accumulated so far by us
     * NOTE: is added after each Deal completes
     */
    totalScoreUs = "totalScoreUs",
    /**
     * Total score over all Deals accumulated so far by them
     * NOTE: is added after each Deal completes
     */
    totalScoreThem = "totalScoreThem",
    /**
     * Sum up value of all contract values called by us (won|lost)
     * NOTE: Coinche|Contree only
     */
    valueContractsUs = "valueContractsUs",
    /**
     * Capots announced by us - ANONUNCED only
     */
    numCapotsCalledUs = "numCapotsCalledUs",
    /**
     * Capots won by us - ANNOUNCED AND UNANNOUNCED
     */
    numCapotsWonUs = "numCapotsWonUs",
    /**
     * Generales announced by us - ANONUNCED only
     */
    numGeneralesCalledUs = "numGeneralesCalledUs",
    /**
     * Generales won by us - ANNOUNCED AND UNANNOUNCED
     */
    numGeneralesWonUs = "numGeneralesWonUs",
    /**
     * Coinches announced by us || Doubles announced by us
     */
    numContrasCalledUs = "numContrasCalledUs",
    /**
     * Coinches won by us || Doubles won by us
     */
    numContrasWonUs = "numContrasWonUs",
    /**
     * Coinches announced by them || Doubles announced by them
     */
    numContrasCalledThem = "numContrasCalledThem",
    /**
     * Coinches won by them || Doubles won by them
     */
    numContrasWonThem = "numContrasWonThem",
    /**
     * Surcoinches announced by us || Redoubles announced by us
     */
    numReContrasCalledUs = "numReContrasCalledUs",
    /**
     * Surcoinches won by us || Redoubles won by us
     */
    numReContrasWonUs = "numReContrasWonUs",
}

/**
 * Calculate these stats to display in Statistics Screen
 */
export enum ImmediateStatsItemKeys {
    /**
     * Number of Games played
     */
    numGamesPlayed = "numGamesPlayed",
    /**
     * Number of Games won by us
     */
    numGamesWonUs = "numGamesWonUs",
    /**
     * Percentage of Games won by us
     */
    percGamesWonUs = "percGamesWonUs",
    /**
     * Number of Deals played
     */
    numDealsPlayed = "numDealsPlayed",
    /**
     * Total points scored by us
     */
    totalScoreUs = "totalScoreUs",
    /**
     * Total points scored by them
     */
    totalScoreThem = "totalScoreThem",
    /**
     * Average points per deal scored by us
     */
    avgScorePerDealUs = "avgScorePerDealUs",
    /**
     * Average points per deal scored by them
     */
    avgScorePerDealThem = "avgScorePerDealThem",
    /**
     * Average score difference between us and them
     * (totalScoreUs - totalScoreThem) / numDealsPlayed [%]
     */
    avgScoreDifference = "avgScoreDifference",
    /**
     * Percentage of deals we were in attack
     */
    percAttackDealsUs = "percAttackDealsUs",
    /**
     * Percentage of deals we were in attack and won
     */
    percAttackDealsWonUs = "percAttackDealsWonUs",
    /**
     * Percentage of deals we were in defense
     */
    percDefenseDealsUs = "percDefenseDealsUs",
    /**
     * Percentage of deals we were in defense and won
     */
    percDefenseDealsWonUs = "percDefenseDealsWonUs",
    /**
     * Percentage of deals (live) Player was in attack
     */
    percAttackDealsPlayer = "percAttackDealsPlayer",
    /**
     * Percentage of deals (live) Player was in attack and won
     */
    percAttackDealsWonPlayer = "percAttackDealsWonPlayer",
    /**
     * Average value of contracts ANNOUNCED by use (even those lost)
     */
    avgContractValueUs = "avgContractValueUs",
    /**
     * Number of Capot contracts announced by us
     */
    numCapotsCalledUs = "numCapotsCalledUs",
    /**
     * Number of Capot contracts won by us
     */
    numCapotsWonUs = "numCapotsWonUs",
    /**
     * Number of Generale contracts announced by us
     */
    numGeneralesCalledUs = "numGeneralesCalledUs",
    /**
     * Number of Generale contracts won by us
     */
    numGeneralesWonUs = "numGeneralesWonUs",
    
    /**
     * Number of Coinches announced by us
     */
    numCoinchesCalledUs = "numCoinchesCalledUs",
    /**
     * Number of Coinches won by us:
     * - Coinches We declared and Won
     * - Coinches They declared but We Won
     */
    numCoinchesWonUs = "numCoinchesWonUs",
    /**
     * Number of Surcoinches announced by us
     */
    numSurCoinchesCalledUs = "numSurCoinchesCalledUs",
    /**
     * Number of Surcoinches won by us:
     * - Surcoinches We declared and Won
     * - Surcoinches They declared but We Won
     */
    numSurCoinchesWonUs = "numSurCoinchesWonUs",
    /**
     * Number of Coinches announced by Them
     */
    numCoinchesCalledThem = "numCoinchesCalledThem",
    /**
     * Number of Coinches won by Them:
     * - Coinches They declared and Won
     * - Coinches We declared but They Won
     */
    numCoinchesWonThem = "numCoinchesWonThem",
    
    /**
     * Number of Doubles announced by us
     */
    numDoublesCalledUs = "numDoublesCalledUs",
    /**
     * Number of Doubles won by us:
     * - Doubles We declared and Won
     * - Doubles They declared but We Won
     */
    numDoublesWonUs = "numDoublesWonUs",
    /**
     * Number of Redoubles announced by us
     */
    numReDoublesCalledUs = "numReDoublesCalledUs",
    /**
     * Number of Redoubles won by us:
     * - Redoubles We declared and Won
     * - Redoubles They declared but We Won
     */
    numReDoublesWonUs = "numReDoublesWonUs",
    /**
     * Number of Doubles announced by them
     */
    numDoublesCalledThem = "numDoublesCalledThem",
    /**
     * Number of Doubles won by them
     * - Doubles They declared and Won
     * - Doubles We declared but They Won
     */
    numDoublesWonThem = "numDoublesWonThem",
}

/**
 * @see OptionsDataManager
 */
export class StatisticsDataManager
{
    private static scene: GameScene;
    /**
     * Statistics intermediate/live data by variationId
     */
    public static data: StatisticsLiveData[];
    public static dataDefaults: StatisticsLiveData[];

    /**
     * For saving items data
     */
    private static variationId: VariationId;
    // private static aiLevel: AILevel = null;

    /**
     * For saving items data
     */
    public static SetVariationId(variationId: VariationId): void
    {
        this.variationId = variationId;
    }

    // public static SetAILevel(aiLevel: AILevel): void
    // {
    //     this.aiLevel = aiLevel;
    // }

    /**
     * Preload statistics variations data for all input variation Ids from HDD
     * - ease of use, no need to load/delete/load new/save, ONLY 'load once on game load' and 
     *   save anytime (automatically save ALL dirty or save by variationId [supply index externally]).
     * - good for lobby, where we can scroll items and click STATS arbitrarily (do not want to load/unload async files for that)
     * 
     * @param variationIds 
     */
    public static Init(scene: GameScene, preloadVariationIds: VariationId[]): void
    {
        this.scene = scene;

        this.dataDefaults = [];
        this.dataDefaults[VariationId.BELOTE] = belote_statistics_default;
        this.dataDefaults[VariationId.COINCHE] = coinche_statistics_default;
        this.dataDefaults[VariationId.CONTREE] = contree_statistics_default;

        this.data = [];
        preloadVariationIds.forEach((variationId: VariationId)=>{
            // cache variation stats data
            this.data[variationId] = AppLocalStorage.GetStatisticsData(
                variationId,               // grab live data by this id
                JSONDeepClone(this.dataDefaults[variationId]),  // in case of no live data, grab default data
            );
        });
    }

    public static SetDefaultsAll(saveData?: boolean): void
    {
        this.dataDefaults.forEach((variationData: StatisticsLiveData, variationId: VariationId)=>{
            this.SetDefaults(variationId, saveData);
        });
    }

    public static SetDefaults(variationId: VariationId, saveData?: boolean): void
    {
        this.data[variationId] = JSONDeepClone(this.dataDefaults[variationId]);
        if (saveData) {
            AppLocalStorage.SetStatisticsData(variationId, this.data[variationId]);
            this.scene.statisticsScreen.SetDirty();
        }
    }

    public static GetVariationData(variationId?: VariationId): StatisticsLiveData
    {
        variationId = variationId ?? this.variationId ?? AppLocalization.GetCurVariationId();
        // console.log("GetVariationData",VariationId[variationId]);

        return this.data[variationId];
    }

    /**
     * Saves stats data of specific variation to HDD.
     * 
     * @param variationId If supplied, saves data of supplied variation to hdd, 
     *                    otherwise of variationId set in @see AppLocalization.
     */
    public static SaveVariationData(variationId?: VariationId): void
    {
        variationId = variationId ?? this.variationId ?? AppLocalization.GetCurVariationId();
        AppLocalStorage.SetStatisticsData(this.variationId, this.data[this.variationId]);
    }

    public static SetIntermediateStatByKey(key: IntermediateStatsItemKeys, value?: any, variationId?: VariationId, aiLevel?: AILevel): void
    {
        variationId = variationId ?? this.variationId ?? AppLocalization.GetCurVariationId();
        aiLevel = aiLevel ?? this.scene.tableScreen.GetTableAI().GetAILevel();
        // console.log(AILevel[aiLevel]);

        // const itemIndex = this.dataItemIndexByKey[variationId][key];
        // console.log(itemIndex, VariationId[this.variationId], DifficultyId[this.aiLevel]);
        // console.log(this.data[this.variationId]);
        // console.log(this.data[this.variationId].categories[this.aiLevel]);
        // console.log(this.data[this.variationId].categories[this.aiLevel].items[itemIndex]);
        
        const cachedValue: number = this.data[variationId].difficulties[aiLevel][key];
        let finalValue = cachedValue + value;
        
        // console.log("save: finalValue:",finalValue);
        this.data[variationId].difficulties[aiLevel][key] = finalValue;
    }

    public static GetIntermediateStatByKey(key: IntermediateStatsItemKeys, variationId?: VariationId, aiLevel?: AILevel): number
    {
        variationId = variationId ?? this.variationId ?? AppLocalization.GetCurVariationId();
        aiLevel = aiLevel ?? this.scene.tableScreen.GetTableAI().GetAILevel();
        
        // const itemIndex = this.dataItemIndexByKey[variationId][key];
        const itemValue: number = this.data[variationId].difficulties[aiLevel][key];
        
        return itemValue;
    }
}